import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SERVICES, pick } from "@/data/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT_CLS = "rounded-xl focus-visible:ring-orange-500";

export default function EnquiryForm() {
  const location = useLocation();
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location.state?.estimate) {
      setForm((f) => ({ ...f, message: location.state.estimate }));
    }
  }, [location.state]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("form.errRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service || null,
        message: form.message.trim() || null,
      });
      toast.success(t("form.success"));
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(t("form.errFail"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form data-testid="contact-enquiry-form" onSubmit={submit} className="bg-white border border-orange-100 rounded-2xl shadow-xl shadow-orange-500/5 p-6 sm:p-8 space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{t("form.name")}</label>
          <Input data-testid="enquiry-name-input" value={form.name} onChange={set("name")} placeholder={t("form.namePh")} required className={INPUT_CLS} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{t("form.phone")}</label>
          <Input data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." required className={INPUT_CLS} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{t("form.email")}</label>
          <Input data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={INPUT_CLS} />
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{t("form.service")}</label>
          <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
            <SelectTrigger data-testid="enquiry-service-select" className="rounded-xl">
              <SelectValue placeholder={t("form.servicePh")} />
            </SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => (
                <SelectItem key={s.id} value={s.title} data-testid={`enquiry-service-${s.id}`}>
                  {pick(s, "title", lang)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">{t("form.details")}</label>
        <Textarea
          data-testid="enquiry-message-input"
          rows={5}
          value={form.message}
          onChange={set("message")}
          placeholder={t("form.msgPh")}
          className={INPUT_CLS}
        />
      </div>
      <button
        type="submit"
        data-testid="submit-enquiry-button"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold px-8 py-3.5 rounded-full hover:from-orange-600 hover:to-amber-600 transition-colors duration-200 disabled:opacity-60 shadow-lg shadow-orange-500/30"
      >
        {submitting ? t("form.submitting") : t("form.submit")} <Send className="w-4 h-4" />
      </button>
    </form>
  );
}
